import { defineStore } from 'pinia'
import client from '../api/client'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    checked: false,
  }),

  getters: {
    isAuthenticated: (state) => Boolean(state.user),
    isAdmin: (state) => state.user?.role === 'admin',
  },

  actions: {
    async fetchCurrentUser() {
      try {
        const { data } = await client.get('/api/v1/session')
        this.user = data.user
      } catch {
        this.user = null
      } finally {
        this.checked = true
      }
    },

    async login(email, password) {
      const { data } = await client.post('/api/v1/session', {
        user: { email, password },
      })
      this.user = data.user
      this.checked = true
    },

    async register(email, password, passwordConfirmation, { vehicle = null, vehiclePhotos = [] } = {}) {
      if (vehicle?.make) {
        const formData = new FormData()
        formData.append('user[email]', email)
        formData.append('user[password]', password)
        formData.append('user[password_confirmation]', passwordConfirmation)
        formData.append('vehicle[make]', vehicle.make)
        if (vehicle.equipment) formData.append('vehicle[equipment]', vehicle.equipment)
        vehiclePhotos.forEach((photo) => formData.append('photos[]', photo))
        const { data } = await client.post('/api/v1/registration', formData)
        return data
      }

      const { data } = await client.post('/api/v1/registration', {
        user: { email, password, password_confirmation: passwordConfirmation },
      })
      return data
    },

    async confirmEmail(token) {
      const { data } = await client.get('/api/v1/confirmation', {
        params: { confirmation_token: token },
      })
      return data
    },

    async resendConfirmation(email) {
      const { data } = await client.post('/api/v1/confirmation', {
        user: { email },
      })
      return data
    },

    async requestPasswordReset(email) {
      const { data } = await client.post('/api/v1/password', {
        user: { email },
      })
      return data
    },

    async resetPassword(token, password, passwordConfirmation) {
      const { data } = await client.patch('/api/v1/password', {
        user: {
          reset_password_token: token,
          password,
          password_confirmation: passwordConfirmation,
        },
      })
      return data
    },

    async logout() {
      await client.delete('/api/v1/session')
      this.user = null
    },

    async updateProfile(
      name,
      username,
      {
        avatar = null,
        removeAvatar = false,
        profilePublic = false,
        city = '',
        cityLat = null,
        cityLng = null,
        about = '',
      } = {}
    ) {
      let data

      if (avatar || removeAvatar) {
        const formData = new FormData()
        formData.append('user[name]', name ?? '')
        formData.append('user[username]', username ?? '')
        formData.append('user[profile_public]', profilePublic ? '1' : '0')
        formData.append('user[city]', city ?? '')
        if (cityLat != null) {
          formData.append('user[city_lat]', String(cityLat))
        }
        if (cityLng != null) {
          formData.append('user[city_lng]', String(cityLng))
        }
        formData.append('user[about]', about ?? '')
        if (avatar) {
          formData.append('avatar', avatar)
        }
        if (removeAvatar) {
          formData.append('remove_avatar', '1')
        }
        ;({ data } = await client.patch('/api/v1/profile', formData))
      } else {
        ;({ data } = await client.patch('/api/v1/profile', {
          user: {
            name,
            username,
            profile_public: profilePublic,
            city,
            city_lat: cityLat,
            city_lng: cityLng,
            about,
          },
        }))
      }

      this.user = data.user
    },
  },
})
